import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  Radio,
  RadioGroup,
  FormLabel,
} from "@mui/material";
import { motion } from "framer-motion";
import Logo from "components/logo";
import { useSite } from "hooks/use-site";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store";
import RetailCountriesTable from "./components/countries-table";
import RetailCurrenciesTable from "./components/currencies-table";
import RetailIndividualsTable from "./components/clients-table";
import CorporateCountriesTable from "./components/corporate-countries-table";
import CorporateClientsTable from "./components/corporate-clients-table";
import CorporateCurrenciesTable from "./components/corporate-currencies-table";
import { updateUserAccount } from "../../../service/api";
import { errorNotification } from "../../../features/notifications/notificationsSlice";
import { useAccountQuery } from "../../../features/auth/authApi";

export type RetailCountry = {
  country: string;
  percentage: string;
};

export type RetailCurrency = {
  currency: string;
  percentage: string;
};

export type RetailIndividual = {
  percentage: string;
};

export type CorporateCountry = {
  country: string;
  percentage: string;
};

export type CorporateCurrency = {
  currency: string;
  percentage: string;
};

export type CorporateIndividual = {
  name: string;
  percentage: string;
};

type InitialValues = {
  areOtherCompanies: boolean | null;
  jurisdictionsAndReasons: string;
  ps_derivatives: boolean;
  ps_bond: boolean;
  ps_markets: boolean;
  ps_crypto: boolean;
  ps_hedge_funds: boolean;
  ps_swaps: boolean;
  ps_futures: boolean;
  ps_other: string;
  psps: string;
  wasCompanyTerminated: boolean | null;
  terminationReason: string;
  hasMLRO: boolean | null;
  isAMLCTFAudited: boolean | null;
  hasAddressedAMLCTFRisks: boolean | null;
  engagesInHighRiskJurisdictions: boolean | null;
  enhancedDueDiligence: boolean | null;
  anonymousAccountOffered: boolean | null;
  policiesForShellBanks: boolean | null;
  clientScreenings: boolean | null;
  engageWithPeps: boolean | null;
  verifyClientIdentity: boolean | null;
  uboDueDiligence: boolean | null;
  minUboShareholdingPercentage: string;

  acceptRetailClients: boolean | null;
  retailCountries: RetailCountry[];
  retailCurrencies: RetailCurrency[];
  retailIndividuals: RetailIndividual[];

  acceptCorporateClients: boolean | null;
  corporateCountries: CorporateCountry[];
  corporateCurrencies: CorporateCurrency[];
  corporateIndividuals: CorporateIndividual[];
};

const initialValues: InitialValues = {
  areOtherCompanies: null,
  jurisdictionsAndReasons: "",
  ps_derivatives: false,
  ps_bond: false,
  ps_markets: false,
  ps_crypto: false,
  ps_hedge_funds: false,
  ps_swaps: false,
  ps_futures: false,
  ps_other: "",
  psps: "",
  wasCompanyTerminated: null,
  terminationReason: "",
  hasMLRO: null,
  isAMLCTFAudited: null,
  hasAddressedAMLCTFRisks: null,
  engagesInHighRiskJurisdictions: null,
  enhancedDueDiligence: null,
  anonymousAccountOffered: null,
  policiesForShellBanks: null,
  clientScreenings: null,
  engageWithPeps: null,
  verifyClientIdentity: null,
  uboDueDiligence: null,
  minUboShareholdingPercentage: "",

  acceptRetailClients: null,
  retailCountries: [{ country: "", percentage: "" }],
  retailCurrencies: [{ currency: "", percentage: "" }],
  retailIndividuals: [{ percentage: "" }],

  acceptCorporateClients: null,
  corporateCountries: [{ country: "", percentage: "" }],
  corporateCurrencies: [{ currency: "", percentage: "" }],
  corporateIndividuals: [{ name: "", percentage: "" }],
};

const validationSchema = Yup.object()
  .shape({
    areOtherCompanies: Yup.boolean().required("Required"),
    jurisdictionsAndReasons: Yup.string().when("areOtherCompanies", {
      is: "YES",
      then: Yup.string().required("Please specify the jurisdictions/reasons"),
    }),
    psps: Yup.string().required("Required"),
    wasCompanyTerminated: Yup.boolean().required("Required"),
    terminationReason: Yup.string().when("wasCompanyTerminated", {
      is: "YES",
      then: Yup.string().required("Please provide the reason for termination"),
    }),
    hasMLRO: Yup.boolean().required("Required"),
    isAMLCTFAudited: Yup.boolean().required("Required"),
    hasAddressedAMLCTFRisks: Yup.boolean().required("Required"),
    engagesInHighRiskJurisdictions: Yup.boolean().required("Required"),
    enhancedDueDiligence: Yup.boolean().required("Required"),
    anonymousAccountOffered: Yup.boolean().required("Required"),
    policiesForShellBanks: Yup.boolean().required("Required"),
    clientScreenings: Yup.boolean().required("Required"),
    engageWithPeps: Yup.boolean().required("Required"),
    verifyClientIdentity: Yup.boolean().required("Required"),
    uboDueDiligence: Yup.boolean().required("Required"),
    acceptRetailClients: Yup.boolean().required("Required"),
    acceptCorporateClients: Yup.boolean().required("Required"),
    minUboShareholdingPercentage: Yup.number()
      .typeError("Must be a number")
      .min(1, "Can not be less then 1")
      .required("Required"),
  })
  .test("at-least-one", "Select at least one product/service", (values) => {
    return (
      values?.ps_derivatives ||
      values?.ps_bond ||
      values?.ps_markets ||
      values?.ps_crypto ||
      values?.ps_hedge_funds ||
      values?.ps_swaps ||
      values?.ps_futures ||
      (values?.ps_other && values.ps_other !== "")
    );
  });

const CompanyQuestionnaire = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();

  const { logo, frontWalletAuthPageColor } = useSite();
  const [otherSelected, setOtherSelected] = useState(false);

  const onSubmit = async (values: any) => {
    const form4_json = JSON.stringify(values);

    try {
      await updateUserAccount({ form4_json: form4_json });

      localStorage.removeItem("form4_json");
    } catch (err: any) {
      dispatch(errorNotification(err.message || "Update failed"));
    } finally {
      navigate("/separate-corp-info-2");
    }
  };

  const savedForm4 = account?.user.form4_json;
  let parsedForm4: Partial<InitialValues> = {};
  if (savedForm4) {
    if (typeof savedForm4 === "object") {
      parsedForm4 = savedForm4;
    } else {
      try {
        parsedForm4 = JSON.parse(savedForm4);
      } catch {
        console.warn("Cant parse form4_json");
      }
    }
  }
  const formEDDInitialValues: InitialValues = {
    ...initialValues,
    ...parsedForm4,
  };

  const {
    handleSubmit,
    getFieldMeta,
    handleBlur,
    values,
    isValid,
    isSubmitting,
    setFieldValue,
    handleChange,
  } = useFormik<InitialValues>({
    initialValues: formEDDInitialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Box display={"flex"} alignItems="center" justifyContent="center">
      <Container maxWidth="lg">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box
              paddingY={2.5}
              paddingX={4}
              sx={{ bgcolor: frontWalletAuthPageColor }}
            >
              <Logo src={logo} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Typography variant="h5" fontWeight={"600"}>
                    Company Information
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Typography variant="h6" fontWeight={"600"}>
                    Please complete the following information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        Are there any offshore companies within your business
                        structure?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="areOtherCompanies"
                        value={
                          values.areOtherCompanies === null
                            ? ""
                            : values.areOtherCompanies
                            ? "yes"
                            : "no"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "areOtherCompanies",
                            e.target.value === "yes"
                          )
                        }
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {values.areOtherCompanies && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Please specify jurisdictions/reasons"
                        name="jurisdictionsAndReasons"
                        value={values.jurisdictionsAndReasons}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          getFieldMeta("jurisdictionsAndReasons").touched &&
                          !!getFieldMeta("jurisdictionsAndReasons").error
                        }
                        helperText={
                          getFieldMeta("jurisdictionsAndReasons").touched &&
                          getFieldMeta("jurisdictionsAndReasons").error
                        }
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Which products/services do you offer?
                    </Typography>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_derivatives}
                            onChange={(e) =>
                              setFieldValue("ps_derivatives", e.target.checked)
                            }
                          />
                        }
                        label="Derivatives and Options"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_bond}
                            onChange={(e) =>
                              setFieldValue("ps_bond", e.target.checked)
                            }
                          />
                        }
                        label="Bond"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_markets}
                            onChange={(e) =>
                              setFieldValue("ps_markets", e.target.checked)
                            }
                          />
                        }
                        label="Markets"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_crypto}
                            onChange={(e) =>
                              setFieldValue("ps_crypto", e.target.checked)
                            }
                          />
                        }
                        label="Cryptocurrencies"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_hedge_funds}
                            onChange={(e) =>
                              setFieldValue("ps_hedge_funds", e.target.checked)
                            }
                          />
                        }
                        label="Hedge Funds"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_swaps}
                            onChange={(e) =>
                              setFieldValue("ps_swaps", e.target.checked)
                            }
                          />
                        }
                        label="Swaps"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ps_futures}
                            onChange={(e) =>
                              setFieldValue("ps_futures", e.target.checked)
                            }
                          />
                        }
                        label="Futures"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={otherSelected}
                            onChange={(e) => {
                              setOtherSelected(e.target.checked);
                              if (!e.target.checked) {
                                setFieldValue("ps_other", "");
                              }
                            }}
                          />
                        }
                        label="Other"
                      />
                    </FormGroup>
                    {!values.ps_derivatives &&
                      !values.ps_bond &&
                      !values.ps_markets &&
                      !values.ps_crypto &&
                      !values.ps_hedge_funds &&
                      !values.ps_swaps &&
                      !values.ps_futures &&
                      (!values.ps_other || values.ps_other.trim() === "") && (
                        <Typography color="error" variant="caption">
                          Select at least one product/service.
                        </Typography>
                      )}
                    {otherSelected && (
                      <TextField
                        fullWidth
                        label="Other (please specify)"
                        name="ps_other"
                        value={values.ps_other}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          getFieldMeta("ps_other").touched &&
                          !!getFieldMeta("ps_other").error
                        }
                        helperText={
                          getFieldMeta("ps_other").touched &&
                          getFieldMeta("ps_other").error
                        }
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="With which PSPs are you working?"
                      name="psps"
                      value={values.psps}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("psps").touched &&
                        !!getFieldMeta("psps").error
                      }
                      helperText={
                        getFieldMeta("psps").touched &&
                        getFieldMeta("psps").error
                      }
                    />
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Was the company&apos;s account terminated or suspended
                          by any financial services provider?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="wasCompanyTerminated"
                          value={
                            values.wasCompanyTerminated === null
                              ? ""
                              : values.wasCompanyTerminated
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "wasCompanyTerminated",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {values.wasCompanyTerminated && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Please state the reason"
                          name="terminationReason"
                          value={values.terminationReason}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            getFieldMeta("terminationReason").touched &&
                            !!getFieldMeta("terminationReason").error
                          }
                          helperText={
                            getFieldMeta("terminationReason").touched &&
                            getFieldMeta("terminationReason").error
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Does your company have a MLRO?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="hasMLRO"
                          value={
                            values.hasMLRO === null
                              ? ""
                              : values.hasMLRO
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue("hasMLRO", e.target.value === "yes")
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Has your company’s AML/CTF procedures been audited by
                          an independent external auditor in the last 24 months?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="isAMLCTFAudited"
                          value={
                            values.isAMLCTFAudited === null
                              ? ""
                              : values.isAMLCTFAudited
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "isAMLCTFAudited",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Are you involved in activities aimed at reducing
                          AML/CTF risks, implementing restrictions, and risk
                          management?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="hasAddressedAMLCTFRisks"
                          value={
                            values.hasAddressedAMLCTFRisks === null
                              ? ""
                              : values.hasAddressedAMLCTFRisks
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "hasAddressedAMLCTFRisks",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Do you engage in transactions involving high-risk
                          jurisdictions?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="engagesInHighRiskJurisdictions"
                          value={
                            values.engagesInHighRiskJurisdictions === null
                              ? ""
                              : values.engagesInHighRiskJurisdictions
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "engagesInHighRiskJurisdictions",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Do you conduct enhanced due diligence for transactions
                          involving high-risk jurisdictions?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="enhancedDueDiligence"
                          value={
                            values.enhancedDueDiligence === null
                              ? ""
                              : values.enhancedDueDiligence
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "enhancedDueDiligence",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Are anonymous account opening/servicing options
                          provided by your company?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="anonymousAccountOffered"
                          value={
                            values.anonymousAccountOffered === null
                              ? ""
                              : values.anonymousAccountOffered
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "anonymousAccountOffered",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Are there clear and unconditional company’s policies
                          that forbid establishing a correspondent relationship
                          with a shell bank, as well as opening accounts or
                          providing services to them?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="policiesForShellBanks"
                          value={
                            values.policiesForShellBanks === null
                              ? ""
                              : values.policiesForShellBanks
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "policiesForShellBanks",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Do you conduct screenings of your clients and
                          transactions to identify politically exposed persons
                          (PEPs) and individuals/entities subject to sanctions?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="clientScreenings"
                          value={
                            values.clientScreenings === null
                              ? ""
                              : values.clientScreenings
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "clientScreenings",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Are you willing to engage in business transactions
                          with politically exposed persons (PEPs)?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="engageWithPeps"
                          value={
                            values.engageWithPeps === null
                              ? ""
                              : values.engageWithPeps
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "engageWithPeps",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Is it mandatory for your company to verify the
                          identity of clients during the account opening process
                          and keep their identification information on record?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="verifyClientIdentity"
                          value={
                            values.verifyClientIdentity === null
                              ? ""
                              : values.verifyClientIdentity
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "verifyClientIdentity",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Is the identification of the UBO part of your
                          company&apos;s due diligence process for each
                          Corporate client?
                        </FormLabel>
                        <RadioGroup
                          row
                          name="uboDueDiligence"
                          value={
                            values.uboDueDiligence === null
                              ? ""
                              : values.uboDueDiligence
                              ? "yes"
                              : "no"
                          }
                          onChange={(e) =>
                            setFieldValue(
                              "uboDueDiligence",
                              e.target.value === "yes"
                            )
                          }
                          onBlur={handleBlur}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="What is the minimum percentage of shareholding
(%) at which your company requires information
on the beneficial owners?
"
                      name="minUboShareholdingPercentage"
                      value={values.minUboShareholdingPercentage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("minUboShareholdingPercentage").touched &&
                        !!getFieldMeta("minUboShareholdingPercentage").error
                      }
                      helperText={
                        getFieldMeta("minUboShareholdingPercentage").touched &&
                        getFieldMeta("minUboShareholdingPercentage").error
                      }
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">Customer Base</Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Do you accept Individual clients?
                    </FormLabel>
                    <RadioGroup
                      row
                      name="acceptRetailClients"
                      value={
                        values.acceptRetailClients === null
                          ? ""
                          : values.acceptRetailClients
                          ? "yes"
                          : "no"
                      }
                      onChange={(e) =>
                        setFieldValue(
                          "acceptRetailClients",
                          e.target.value === "yes"
                        )
                      }
                      onBlur={handleBlur}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {values.acceptRetailClients && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle1" gutterBottom>
                          Countries
                        </Typography>
                        <RetailCountriesTable
                          values={values.retailCountries}
                          setFieldValue={setFieldValue}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle1" gutterBottom>
                          Currencies
                        </Typography>
                        <RetailCurrenciesTable
                          values={values.retailCurrencies}
                          setFieldValue={setFieldValue}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle1" gutterBottom>
                          Individuals
                        </Typography>
                        <RetailIndividualsTable
                          values={values.retailIndividuals}
                          setFieldValue={setFieldValue}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Do you accept Corporate clients?
                    </FormLabel>
                    <RadioGroup
                      row
                      name="acceptCorporateClients"
                      value={
                        values.acceptCorporateClients === null
                          ? ""
                          : values.acceptCorporateClients
                          ? "yes"
                          : "no"
                      }
                      onChange={(e) =>
                        setFieldValue(
                          "acceptCorporateClients",
                          e.target.value === "yes"
                        )
                      }
                      onBlur={handleBlur}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {values.acceptCorporateClients && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        Countries
                      </Typography>
                      <CorporateCountriesTable
                        values={values.corporateCountries}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        Currencies
                      </Typography>
                      <CorporateCurrenciesTable
                        values={values.corporateCurrencies}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        Corporate Clients
                      </Typography>
                      <CorporateClientsTable
                        values={values.corporateIndividuals}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                  </Grid>
                )}
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={2}
                    alignItems="center"
                  >
                    <LoadingButton
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      type="submit"
                      disabled={!isValid || isSubmitting}
                    >
                      Submit
                    </LoadingButton>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default CompanyQuestionnaire;
